import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { AlertOctagon } from "lucide-react";
import { EarlyDelivery, apiErrorMessage } from "../lib/api";
import { TASK_TYPE_LABELS } from "../lib/constants";

/**
 * Sales Detail action — opens a dialog to request Early Delivery approval.
 *
 * Visibility rules (enforced upstream; this component just renders when invoked):
 *   • Sale released (not draft, not delivered)
 *   • At least one pending workflow stage
 *   • Active user is DSE / SalesManager / Admin
 *   • Sale has NO pending or approved early-delivery request
 *
 * Confirmation popup mirrors the spec — title, message, mandatory reason
 * textarea, Cancel / Submit Request buttons.
 */
export default function EarlyDeliveryRequestButton({ sale, pendingItems = [], onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please enter a reason for early delivery");
      return;
    }
    setBusy(true);
    try {
      await EarlyDelivery.request(sale.id, { reason: reason.trim() });
      toast.success("Early delivery request submitted for Accounts approval");
      setOpen(false);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="gap-1.5 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
        data-testid="early-delivery-request-btn"
      >
        <AlertOctagon className="h-4 w-4" /> Request Early Delivery
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-amber-600" />
              Request Early Delivery
            </DialogTitle>
            <DialogDescription>
              This vehicle has pending processes. Do you want to submit for Early Delivery approval?
            </DialogDescription>
          </DialogHeader>

          {pendingItems.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                Pending workflow stages
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="early-delivery-pending-list">
                {pendingItems.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200"
                  >
                    {TASK_TYPE_LABELS[t] || t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ed-reason" className="text-xs font-semibold">
              Reason for early delivery *
            </Label>
            <Textarea
              id="ed-reason"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for early delivery request"
              disabled={busy}
              data-testid="early-delivery-reason"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="early-delivery-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !reason.trim()}
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300"
              data-testid="early-delivery-submit-btn"
            >
              {busy ? "Submitting…" : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
